import { enumKeyFromValue, plusBusinessDays } from '@/helpers';
import { t } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { Model } from 'pinia-orm';
import { Attr, BelongsTo, HasOne, MorphMany, MorphOne, Num, Str } from 'pinia-orm/dist/decorators';
import Comment from './Comment';
import ContractEvent from './ContractEvent';
import Correspondence from './Correspondence';
import Project from './Project';
import User from './User';
import VariationOrder from './VariationOrder';
/* --- user header --- */
/* --- end user header --- */

export const DelayStatus = {
  raised: 0,
  registered: 1,
  submitted: 2,
  provisioned: 3,
  approved: 4,
  completed: 5,
  declined: 6,
} as const;

export const DelayStatusTitles = {
  raised: 'Raised',
  registered: 'Registered',
  submitted: 'Submitted',
  provisioned: 'Provisioned',
  approved: 'Approved',
  completed: 'Completed',
  declined: 'Declined',
} as const;

export const DelayStatusColours = {
  raised: '#B80D15',
  registered: '#A6ACEC',
  submitted: '#215F9A',
  provisioned: '#163E64',
  approved: '#0E2841',
  completed: '#196B24',
  declined: '#000',
} as const;

export const DelayStatusTextColours = {
  raised: '#FFF',
  registered: '#000',
  submitted: '#000',
  provisioned: '#FFF',
  approved: '#FFF',
  completed: '#FFF',
  declined: '#FFF',
} as const;

export const DelayCorrespondenceTypes = {
  'notice-of-delay': 1,
  'extension-of-time-submission': 2,
  'approval-of-delay': 3,
} as const;

export default class Delay extends Model {
  static entity = 'App\\Models\\Delay';
  // fields
  @Num(0)
  declare id: number;
  @Str('')
  declare code: string;
  @Str('')
  declare description: string;
  @Num(0)
  declare project_id: number;
  @Num(0)
  declare user_id: number;
  @Num(0)
  declare next_action_assignee_id: number;
  @Str('')
  declare deleted_at: string | undefined;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  @Str('')
  declare registered_at: string | undefined;
  @Str('')
  declare submitted_at: string | undefined;
  @Str('')
  declare provisioned_at: string | undefined;
  @Str('')
  declare approved_at: string | undefined;
  @Str('')
  declare notice_sent_at: string | undefined;
  @Str('')
  declare next_action_due_date: string | undefined;
  @Str('')
  declare date_of_occurrence: string | undefined;
  @Str('')
  declare start_date: string | undefined;
  @Str('')
  declare end_date: string | undefined;
  @Str(undefined)
  declare notes: string | undefined;
  @Str(undefined)
  declare client_ref: string | undefined;
  @Str(undefined)
  declare next_action: string | undefined;
  @Attr(undefined)
  declare days: number | undefined;
  @Num(undefined)
  declare status: ObjectValues<typeof DelayStatus>;
  // relations
  @BelongsTo(() => Project, 'project_id', 'id')
  declare project: Project;
  @BelongsTo(() => User, 'user_id', 'id')
  declare user: User;
  @BelongsTo(() => User, 'next_action_assignee_id', 'id')
  declare next_action_assignee: User;
  @HasOne(() => VariationOrder, 'delay_id', 'id')
  declare variation_order: VariationOrder;
  @HasOne(() => ContractEvent, 'delay_id', 'id')
  declare contract_event: ContractEvent;
  @MorphMany(() => Comment, 'commentable_id', 'commentable_type', 'id')
  declare comments: Comment[];
  @MorphMany(() => Correspondence, 'correspondent_id', 'correspondent_type', 'id')
  declare correspondences: Correspondence[];
  /* --- user code --- */

  get statusTitle() {
    return t(`Models.Delay.statusTitles.${enumKeyFromValue(DelayStatus, this.status)}`);
  }

  get statusColour() {
    return DelayStatusColours[enumKeyFromValue(DelayStatus, this.status)];
  }

  get statusTextColour() {
    return DelayStatusTextColours[enumKeyFromValue(DelayStatus, this.status)];
  }

  get createdAt() {
    return this.created_at ? DateTime.fromISO(this.created_at) : undefined;
  }
  get submittedAt() {
    return this.submitted_at ? DateTime.fromISO(this.submitted_at) : undefined;
  }
  get provisionedAt() {
    return this.provisioned_at ? DateTime.fromISO(this.provisioned_at) : undefined;
  }
  get approvedAt() {
    return this.approved_at ? DateTime.fromISO(this.approved_at) : undefined;
  }
  get registeredAt() {
    return this.registered_at ? DateTime.fromISO(this.registered_at) : undefined;
  }
  get noticeSentAt() {
    return this.notice_sent_at ? DateTime.fromISO(this.notice_sent_at) : undefined;
  }
  get dateOfOccurrence() {
    return this.date_of_occurrence ? DateTime.fromISO(this.date_of_occurrence) : undefined;
  }
  get startDate() {
    return this.start_date ? DateTime.fromISO(this.start_date) : undefined;
  }
  get endDate() {
    return this.end_date ? DateTime.fromISO(this.end_date) : undefined;
  }

  get nextActionDueIn() {
    const projectsStore = useProjectsStore();
    const project = projectsStore.models.find(this.project_id);
    let dueDate = undefined;

    if(!this.date_of_occurrence) {
      return undefined;
    }

    if(!this.notice_sent_at && project.head_contract_settings.nod_required) {
      dueDate = project.head_contract_settings.nod_business_days ?
        plusBusinessDays(
          this.dateOfOccurrence,
          project.head_contract_settings.holidays,
          project.head_contract_settings.nod_period,
        ) :
        this.dateOfOccurrence.plus({ days: project.head_contract_settings.nod_period });
    } else if(
      this.status < DelayStatus.submitted && project.head_contract_settings.delay_submission_required
    ) {
      dueDate = project.head_contract_settings.delay_submission_business_days ?
        plusBusinessDays(
          this.dateOfOccurrence,
          project.head_contract_settings.holidays,
          project.head_contract_settings.delay_submission_period,
        ) :
        this.dateOfOccurrence.plus({
          days: project.head_contract_settings.delay_submission_period,
        });
    }

    return dueDate ? Math.ceil(dueDate.diffNow('days').days) : undefined;
  }
  /* --- end user code --- */
}
